import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import {
  SHOP_STATUS_OPEN_ONLINE,
  SHOP_STATUS_OPEN_OFFLINE,
  SHOP_STATUS_CLOSED_ONLINE,
  SHOP_STATUS_CLOSED_OFFLINE,
} from '@chama/utils/constants/status';
import groupby from 'lodash.groupby';
import getDealerStatus from '@chama/utils/functions/dealer-status';
import { isBlocked, isSuspended } from '@chama/utils/functions/is-dealer-blocked-or-suspended';
import './index.css';
import isMainshop from '../../mainDealerFilter';

const propTypes = {
  dealerList: PropTypes.array.isRequired,
  bigger: PropTypes.bool,
};

const defaultProps = {
  bigger: false,
};

const reduceDealersToStatusCount = (accum, dealer) => {
  const { openedDealers, offlineDealers, closedDealers, suspended, blocked, disabled } = accum;
  const status = getDealerStatus(dealer);

  if (isSuspended(dealer)) {
    return {
      ...accum,
      suspended: suspended + 1,
    };
  }

  if (isBlocked(dealer)) {
    return {
      ...accum,
      blocked: blocked + 1,
    };
  }

  if (status === SHOP_STATUS_OPEN_ONLINE) {
    return {
      ...accum,
      openedDealers: openedDealers + 1,
    };
  }

  if (status === SHOP_STATUS_OPEN_OFFLINE) {
    return {
      ...accum,
      openedDealers: openedDealers + 1,
      offlineDealers: offlineDealers + 1,
    };
  }

  if (status === SHOP_STATUS_CLOSED_OFFLINE || status === SHOP_STATUS_CLOSED_ONLINE) {
    return {
      ...accum,
      closedDealers: closedDealers + 1,
      disabled: dealer.isEnabled ? disabled : disabled + 1,
    };
  }

  return accum;
};

const DealersCounter = ({ dealerList, bigger }) => {
  const initialValue = {
    openedDealers: 0,
    offlineDealers: 0,
    closedDealers: 0,
    suspended: 0,
    blocked: 0,
    disabled: 0,
  };

  // make it wok with legacy export. For getting a correct status we merge mainShop with
  // isOpened and products properties from ServiceAreas since mainSHop do not own them anymore
  const mainShopsWithCorrectStatuses = Object.values(
    groupby(Object.values(dealerList), item => item.authorityId),
  )
    .map(serviceAreas => {
      const mainShop = serviceAreas.filter(d => {
        return isMainshop(d.id);
      });
      if (!mainShop) return null;

      const openedAndWorkingServiceAreas = serviceAreas.filter(
        item => item.isOpened && item.products && item.products.length !== 0,
      );
      if (openedAndWorkingServiceAreas.length > 0) {
        return {
          ...mainShop[0],
          isOpened: true,
          products: openedAndWorkingServiceAreas[0].products,
        };
      }
      return mainShop[0];
    })
    .filter(Boolean);

  const dealerStatuses = mainShopsWithCorrectStatuses.reduce(
    reduceDealersToStatusCount,
    initialValue,
  );
  const {
    openedDealers,
    offlineDealers,
    closedDealers,
    suspended,
    blocked,
    disabled,
  } = dealerStatuses;
  const componentClass = classNames('DealersCounter', {
    is_bigger: bigger,
  });
  const notVisible = closedDealers + suspended + blocked;

  return (
    <div className={componentClass}>
      <div className="DealersCounter__content">
        <div className="DealersCounter__dealer_count">
          <div>
            <div className="DealersCounter__count DealersCounter__count--open">
              <span>{openedDealers}</span>
              &nbsp;
              <FormattedMessage
                id={`open${openedDealers > 1 ? '.plural' : ''}`}
                defaultMessage="OPEN"
              />
            </div>
            <span>&nbsp;({offlineDealers} offline)</span>
          </div>
          <div>
            <div className="DealersCounter__count DealersCounter__count--closed">
              <FormattedMessage
                id="dealers_not_visible"
                values={{ count: notVisible }}
                defaultMessage={`${notVisible} NOT VISIBLE IN THE APP`}
              />
            </div>
            <FormattedMessage
              id="dealers_not_visible_details"
              defaultMessage={`(${disabled} disabled)`}
              values={{ count: disabled }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

DealersCounter.propTypes = propTypes;
DealersCounter.defaultProps = defaultProps;

export default DealersCounter;



// WEBPACK FOOTER //
// ../ui-components/components/DealersCounter/index.jsx